import React, { useState } from 'react';
import { trpcSwr } from '../../../../api/trpc';
import moment from 'moment';
import {
  CleanAuditLog,
  CustomsDeliveryOrderStatus,
} from '../../../../export-types/cleaned-types';
import { diffWordsWithSpace } from 'diff';
import classNames from 'classnames';

type Props = {
  id: string;
};

const TranslateMapOrder: Record<string, string> = {
  name: 'Наименование',
  link: 'Ссылка на товар',
  volume: 'Объем',
  weight: 'Вес',
  price: 'Цена',
  amount: 'Кол-во',
  city: 'Город',
  comment: 'Комментарий',
  files: 'Файлы',
  accountId: 'ID плательщика',
};

const TranslateMapPriceData: Record<string, string> = {
  cargoPlaces: 'Кол-во грузовых мест',
  individualLogisticPrice: 'Индивидуальный тариф',
  packagePrice: 'Упаковка',
  vat: 'НДС',
  additionalSupportPrice: 'Сопровождение поставки - доп.расходы',
  additionalCustomsFeePrice: 'Дополнительная там. пошлина',
  usdRubCbrRate: 'Курс $',
  cnyRubCbrRate: 'Курс ¥',
  usdRubCommission: 'Комиссия к $',
  cnyRubCommission: 'Комиссия к ¥',
  customsDeliveryMethod: 'Способ доставки',
  customsDeliverySupport: 'Сопровождение поставки',
  trueSignApplyMarkingRate: 'ЧЗ заказ маркировки',
  trueSignOrderMarkingRate: 'ЧЗ нанесение маркировки',
};

const TranslateMapInvoice: Record<string, string> = {
  id: '№',
  goods: 'Товары',
  isPaid: 'Оплачен',
  createdAt: 'Дата создания',
  updatedAt: 'Дата обновления',
  accountId: 'Плательщик',
  receiverAccountId: 'Получатель',
  uuid: 'UUID',
};
const TranslateMapInvoiceGood: Record<string, string> = {
  unit: 'Ед. изм.',
  amount: 'Кол-во',
  name: 'Наименование',
  price: 'Стоимость',
};

function diffChanges(log: CleanAuditLog) {
  const result = [];
  for (const [key, value] of Object.entries(log.data.diff)) {
    let translatedKey = key;

    if (log.object === 'CustomsDeliveryOrderInvoice') {
      if (translatedKey === 'customsDeliveryOrderId') {
        continue;
      }
      translatedKey = TranslateMapInvoice[key] ? TranslateMapInvoice[key] : key;
    }

    if (log.object === 'CustomsDeliveryOrderPriceData') {
      translatedKey = TranslateMapPriceData[key]
        ? TranslateMapPriceData[key]
        : key;
    }

    if (log.object === 'CustomsDeliveryOrder') {
      translatedKey = TranslateMapOrder[key] ? TranslateMapOrder[key] : key;
    }

    let oldValue = value.old;
    if (null === oldValue || undefined === oldValue) {
      oldValue = '';
    }

    if (Array.isArray(oldValue)) {
      oldValue = oldValue.map((item) => JSON.stringify(item));
    }
    if (`${oldValue}` === '[object Object]') {
      oldValue = JSON.stringify(oldValue);
    }

    let newValue = value.new;
    if (Array.isArray(newValue)) {
      if (key === 'goods') {
        newValue = newValue.map((item) => {
          const translatedItem: Record<string, string> = {};
          Object.keys(item).forEach((itemKey) => {
            const translatedKey = TranslateMapInvoiceGood[itemKey] || itemKey;
            translatedItem[translatedKey] = item[itemKey];
          });
          return JSON.stringify(translatedItem);
        });
      } else {
        newValue = newValue.map((item) => JSON.stringify(item));
      }
    }
    if (`${newValue}` === '[object Object]') {
      newValue = JSON.stringify(newValue);
    }

    if (oldValue === newValue) {
      continue;
    }

    const diff = diffWordsWithSpace(`${oldValue}`, `${newValue}`);
    const row = (
      <div key={key}>
        <span className="fw-500">{translatedKey}:</span>{' '}
        <span>
          {diff.map((entry) => (
            <span
              key={JSON.stringify(entry)}
              className={classNames({
                'color-red': entry.removed,
                'line-through': entry.removed,
                'color-green': entry.added,
              })}
            >
              {entry.value}
            </span>
          ))}
        </span>
      </div>
    );
    result.push(row);
  }

  if (result.length === 0 && log.action !== 'delete') {
    return [<>Без изменений</>];
  }

  return result;
}

const processTextData = (log: CleanAuditLog) => {
  let text: string = '';
  let statusText: string = '';
  let withExpand: boolean = false;
  const diff = log.data.diff;

  if (log.object === 'CustomsDeliveryOrderInvoice') {
    if (log.action === 'insert') {
      text = `создал счет №${log.objectId}`;
    } else {
      text = `изменил счет №${log.objectId}`;
    }
    withExpand = true;
    return { text, withExpand };
  }

  if (log.object === 'CustomsDeliveryOrderPriceData') {
    text = 'изменил расчет стоимости услуг';
    withExpand = true;
    return { text, withExpand };
  }

  if (diff['goods']) {
    if (Array.isArray(diff['goods'].new) && Array.isArray(diff['goods'].old)) {
      if (diff['goods'].new.length > diff['goods'].old.length) {
        text = 'добавил код ТН ВЭД';
      }
      if (diff['goods'].new.length < diff['goods'].old.length) {
        text = 'удалил код ТН ВЭД';
      }
      if (diff['goods'].new.length === diff['goods'].old.length) {
        text = 'изменил код ТН ВЭД';
      }
    }
    return { text };
  }

  if (diff['status']) {
    text = 'изменил стадию';
    const newStatus = diff['status'].new as CustomsDeliveryOrderStatus;
    switch (newStatus) {
      case 'created':
        statusText = 'Черновик';
        break;
      case 'preparing':
        statusText = 'Новый';
        break;
      case 'codeSelected':
        statusText = 'Подобрать код';
        break;
      case 'calculationReady':
        statusText = 'Подготовлен пред.расчет';
        break;
      case 'protectCalculation':
        statusText = 'Защитить расчет';
        break;
      case 'prepareDocs':
        statusText = 'Подготовка документов';
        break;
      case 'prepareInvoice':
        statusText = 'Подготовить счет';
        break;
      case 'declarant':
        statusText = 'Декларант';
        break;
      case 'waitingForPayment':
        statusText = 'Ожидаем оплату';
        break;
      case 'buyOut':
        statusText = 'Выкупить';
        break;
      case 'inWork':
        statusText = 'В пути';
        break;
      case 'inStock':
        statusText = 'На складе';
        break;
      case 'sentToRussia':
        statusText = 'Отправлен в РФ';
        break;
      case 'transferredToTC':
        statusText = 'Переданы в ТК';
        break;
      case 'customs':
        statusText = 'Таможня';
        break;
      case 'transitWarehouse':
        statusText = 'Транзитный склад';
        break;
      case 'received':
        statusText = 'Получены';
        break;
      case 'archived':
        statusText = 'Архив';
        break;
      default:
        break;
    }
    return { text, statusText };
  }
  text = 'изменил данные о заказе';
  withExpand = true;

  return { text, statusText, withExpand };
};
type Log = CleanAuditLog & { diff: React.JSX.Element[]; rowHeight: number };

const LogItem = ({ log }: { log: Log }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { text, statusText, withExpand } = processTextData(log);
  return (
    <div>
      <div className={'d-flex gap-1'} key={log.id}>
        <div>{moment(log.createdAt).format('DD.MM.YYYY HH:mm')}</div>
        <div>{log.user?.name}</div>
        <div>
          {text} <span className={'color-green'}>{statusText}</span>
        </div>
        {withExpand && (
          <div
            className={'ms-2 cursor-pointer color-gray-450 mright-auto'}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded((prevState) => !prevState);
            }}
          >
            {isExpanded ? (
              <i className={`bi bi-chevron-up`} />
            ) : (
              <i className={`bi bi-chevron-down`} />
            )}
          </div>
        )}
      </div>
      {isExpanded && log.diff}
    </div>
  );
};

const CustomsDeliveryOrderLogsBlock = ({ id }: Props) => {
  const [showAll, setShowAll] = useState(false);
  const { data = { logs: [], total: 0 }, isLoading } =
    trpcSwr.customsDelivery.getOrderLogs.useSWR({
      id,
      take: showAll ? undefined : 5,
    });

  const loadMore = () => {
    setShowAll(true);
  };

  const items =
    data?.logs.map((item): Log => {
      const diff = diffChanges(item);
      const rowTextLines = Math.max(diff.length, 2);
      const rowHeight = Math.max(70, rowTextLines * 25 + 16);
      return { ...item, diff, rowHeight };
    }) || [];

  return (
    <div>
      <div className="d-flex flex-row justify-content-between">
        <div className="fs-18 fw-600 color-gray-450">Журнал изменений</div>
      </div>

      {items.map((log, index) => (
        <LogItem log={log} key={index} />
      ))}

      {isLoading && (
        <div className="text-center mt-2">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Загрузка...</span>
          </div>
        </div>
      )}

      {data.total > data.logs.length && !isLoading && data.logs.length > 0 && (
        <div className="mx-1 d-flex flex-row justify-content-center">
          <div className="btn btn-success mt-2" onClick={loadMore}>
            Показать еще <i className={`bi bi-chevron-down`} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomsDeliveryOrderLogsBlock;
